import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((message, type = 'info', duration = 3000) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type, duration }]);

        if (duration > 0) {
            setTimeout(() => {
                setToasts(prev => prev.filter(toast => toast.id !== id));
            }, duration);
        }
    }, []);

    const success = useCallback((message, duration) => showToast(message, 'success', duration), [showToast]);
    const error = useCallback((message, duration) => showToast(message, 'error', duration), [showToast]);
    const info = useCallback((message, duration) => showToast(message, 'info', duration), [showToast]);
    const warning = useCallback((message, duration) => showToast(message, 'warning', duration), [showToast]);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const confirm = useCallback((message) => {
        return new Promise((resolve) => {
            const id = Date.now();
            const handleConfirm = (result) => {
                removeToast(id);
                resolve(result);
            };

            setToasts(prev => [...prev, {
                id,
                message,
                type: 'confirm',
                onConfirm: () => handleConfirm(true),
                onCancel: () => handleConfirm(false)
            }]);
        });
    }, [removeToast]);

    const value = {
        success,
        error,
        info,
        warning,
        confirm,
        removeToast
    };

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
};

const ToastContainer = ({ toasts, removeToast }) => {
    return (
        <div className="fixed top-4 right-4 z-50 space-y-3 max-w-md">
            {toasts.map(toast => (
                <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
            ))}
        </div>
    );
};

const Toast = ({ toast, onClose }) => {
    const { id, message, type, onConfirm, onCancel } = toast;

    const getStyles = () => {
        switch (type) {
            case 'success':
                return {
                    bg: 'bg-green-50 border-green-200',
                    icon: 'check_circle',
                    iconColor: 'text-green-600',
                    textColor: 'text-green-800'
                };
            case 'error':
                return {
                    bg: 'bg-red-50 border-red-200',
                    icon: 'error',
                    iconColor: 'text-red-600',
                    textColor: 'text-red-800'
                };
            case 'warning':
                return {
                    bg: 'bg-yellow-50 border-yellow-200',
                    icon: 'warning',
                    iconColor: 'text-yellow-600',
                    textColor: 'text-yellow-800'
                };
            case 'confirm':
                return {
                    bg: 'bg-cloud border-light-green',
                    icon: 'help_outline',
                    iconColor: 'text-grass',
                    textColor: 'text-dark-green'
                };
            default:
                return {
                    bg: 'bg-gray-50 border-gray-200',
                    icon: 'info',
                    iconColor: 'text-gray-600',
                    textColor: 'text-gray-800'
                };
        }
    };

    const styles = getStyles();

    if (type === 'confirm') {
        return (
            <div className={`${styles.bg} border rounded-xl p-4 shadow-lg backdrop-blur-sm animate-slide-in`}>
                <div className="flex items-start gap-3">
                    <span className={`material-symbols-outlined ${styles.iconColor} text-2xl flex-shrink-0`}>
                        {styles.icon}
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className={`${styles.textColor} font-medium text-sm mb-3`}>{message}</p>
                        <div className="flex gap-2">
                            <button
                                onClick={onConfirm}
                                className="flex-1 px-4 py-2 bg-pear text-dark-green text-sm font-medium rounded-lg hover:bg-grass hover:text-white transition-colors"
                            >
                                Evet
                            </button>
                            <button
                                onClick={onCancel}
                                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                Hayır
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.bg} border rounded-xl p-4 shadow-lg backdrop-blur-sm animate-slide-in`}>
            <div className="flex items-start gap-3">
                <span className={`material-symbols-outlined ${styles.iconColor} text-2xl flex-shrink-0`}>
                    {styles.icon}
                </span>
                <p className={`${styles.textColor} font-medium text-sm flex-1 min-w-0`}>{message}</p>
                <button
                    onClick={onClose}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <span className="material-symbols-outlined text-lg">close</span>
                </button>
            </div>
        </div>
    );
};
